import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, Plus, X } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { api, formatApiError } from "../../api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { useCelebration } from "../../contexts/CelebrationContext";
import { CONTROL } from "../../constants/testIds";

const EMPTY_FORM = {
  org_display_name: "", super_admin_name: "", super_admin_email: "", super_admin_password: "", seat_limit: 10, branch_names: [""],
  billing_amount: "", billing_cycle: "monthly", next_billing_date: "",
};

// UX-CP-01 §2 SCR-P08-04 "Provision new gym" — a straight POST to the existing Phase-00
// `/control/provision`, idempotency-keyed per UI_STANDARDS.md's save/success pattern (same
// spinner->checkmark morph as the gym-side NewMemberDialog).
export function ProvisionGymDialog({ open, onOpenChange }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const queryClient = useQueryClient();
  const { celebrate } = useCelebration();
  const reducedMotion = useReducedMotion();

  const updateBranch = (i, value) => {
    const branches = [...form.branch_names];
    branches[i] = value;
    setForm({ ...form, branch_names: branches });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setStatus("saving");
    try {
      await api.post(
        "/control/provision",
        {
          ...form, seat_limit: Number(form.seat_limit), branch_names: form.branch_names.filter((b) => b.trim()),
          billing_amount: form.billing_amount ? Math.round(Number(form.billing_amount) * 100) : undefined,
          next_billing_date: form.next_billing_date || undefined,
        },
        { headers: { "Idempotency-Key": crypto.randomUUID() } },
      );
      setStatus("success");
      setTimeout(() => {
        celebrate("gym_provisioned", `${form.org_display_name} is live.`);
        queryClient.invalidateQueries({ queryKey: ["control-gyms"] });
        queryClient.invalidateQueries({ queryKey: ["platform-summary"] });
        setForm(EMPTY_FORM);
        setStatus("idle");
        onOpenChange(false);
      }, reducedMotion ? 0 : 600);
    } catch (e2) {
      setError(formatApiError(e2));
      setStatus("idle");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-modal sm:rounded-[14px] max-h-[90vh] overflow-y-auto" data-testid={CONTROL.provisionDialog}>
        <DialogHeader>
          <DialogTitle>Provision a new gym</DialogTitle>
          <DialogDescription>Creates the organization, its first branch(es), a licensed super admin, and an active license — ready to sign in immediately.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="pg-org-name">Gym name <span style={{ color: "var(--scarlet-neg)" }}>*</span></Label>
            <Input id="pg-org-name" data-testid={CONTROL.provisionOrgNameInput} required value={form.org_display_name} onChange={(e) => setForm({ ...form, org_display_name: e.target.value })} />
          </div>

          <div className="space-y-1.5">
            <Label>Branches <span style={{ color: "var(--scarlet-neg)" }}>*</span></Label>
            {form.branch_names.map((b, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input data-testid={CONTROL.provisionBranchInput(i)} required value={b} onChange={(e) => updateBranch(i, e.target.value)} placeholder={`Branch ${i + 1} name`} />
                {form.branch_names.length > 1 && (
                  <button type="button" onClick={() => setForm({ ...form, branch_names: form.branch_names.filter((_, j) => j !== i) })} className="scarlet-focusable p-1.5 rounded-[8px] hover:bg-black/5">
                    <X size={14} />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              data-testid={CONTROL.provisionAddBranch}
              onClick={() => setForm({ ...form, branch_names: [...form.branch_names, ""] })}
              className="scarlet-focusable text-xs font-medium flex items-center gap-1 mt-1"
              style={{ color: "var(--scarlet-accent)" }}
            >
              <Plus size={13} /> Add another branch
            </button>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="pg-admin-name">Super admin name <span style={{ color: "var(--scarlet-neg)" }}>*</span></Label>
            <Input id="pg-admin-name" data-testid={CONTROL.provisionAdminNameInput} required value={form.super_admin_name} onChange={(e) => setForm({ ...form, super_admin_name: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pg-admin-email">Super admin email <span style={{ color: "var(--scarlet-neg)" }}>*</span></Label>
            <Input id="pg-admin-email" type="email" data-testid={CONTROL.provisionAdminEmailInput} required value={form.super_admin_email} onChange={(e) => setForm({ ...form, super_admin_email: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pg-admin-password">Temporary password <span style={{ color: "var(--scarlet-neg)" }}>*</span></Label>
            <Input id="pg-admin-password" type="password" data-testid={CONTROL.provisionAdminPasswordInput} required value={form.super_admin_password} onChange={(e) => setForm({ ...form, super_admin_password: e.target.value })} />
            <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>Min 12 characters, per the gym-side password policy.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pg-seat-limit">Seat limit <span style={{ color: "var(--scarlet-neg)" }}>*</span></Label>
            <Input id="pg-seat-limit" type="number" min={1} data-testid={CONTROL.provisionSeatLimitInput} required value={form.seat_limit} onChange={(e) => setForm({ ...form, seat_limit: e.target.value })} />
          </div>

          {/* DEFECT-D-09: billing terms — optional (a gym can be provisioned before commercial
              terms are finalized, then set later from the Gym Detail page's Billing section). */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5 col-span-1">
              <Label htmlFor="pg-billing-amount">Billing amount (₹)</Label>
              <Input id="pg-billing-amount" type="number" min={0} data-testid={CONTROL.provisionBillingAmountInput} value={form.billing_amount} onChange={(e) => setForm({ ...form, billing_amount: e.target.value })} placeholder="e.g. 15000" />
            </div>
            <div className="space-y-1.5 col-span-1">
              <Label>Billing cycle</Label>
              <Select value={form.billing_cycle} onValueChange={(v) => setForm({ ...form, billing_cycle: v })}>
                <SelectTrigger data-testid={CONTROL.provisionBillingCycleSelect}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="annual">Annual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 col-span-1">
              <Label htmlFor="pg-next-billing">Next billing date</Label>
              <Input id="pg-next-billing" type="date" data-testid={CONTROL.provisionNextBillingInput} value={form.next_billing_date} onChange={(e) => setForm({ ...form, next_billing_date: e.target.value })} />
            </div>
          </div>

          {error && (
            <p className="text-sm shake-on-error" style={{ color: "var(--scarlet-neg)" }} data-testid={CONTROL.provisionError}>
              {error.message} {error.correlationId && <span className="opacity-60">({error.correlationId})</span>}
            </p>
          )}
          <DialogFooter>
            <Button type="submit" disabled={status !== "idle"} data-testid={CONTROL.provisionSubmit} className="text-white" style={{ background: "var(--scarlet-accent)" }}>
              <AnimatePresence mode="wait" initial={false}>
                {status === "saving" && (
                  <motion.span key="saving" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={reducedMotion ? { duration: 0 } : undefined} className="flex items-center gap-2">
                    <Loader2 size={16} className="animate-spin" /> Provisioning…
                  </motion.span>
                )}
                {status === "success" && (
                  <motion.span key="success" initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={reducedMotion ? { duration: 0 } : { type: "spring", stiffness: 300, damping: 15 }} className="flex items-center gap-2">
                    <Check size={16} /> Provisioned
                  </motion.span>
                )}
                {status === "idle" && <motion.span key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={reducedMotion ? { duration: 0 } : undefined}>Provision gym</motion.span>}
              </AnimatePresence>
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
